// Büyüme sayfalarının (Ürün Analizi, Tahminleme, Fiyat & Talep) ortak yapı taşları:
// sayfa başlığı, bölüm kartı, segment seçici, iskelet yükleme ve boş/hata durumu.
import React from 'react';
import { T, radius, tint } from './poyais-ui/theme';
import { Icon } from './poyais-ui/icons';
import { useLang, addTranslations } from './i18n';

addTranslations({
  'GA4 bağlı değil': 'GA4 not connected',
  "Bu özelliğin çalışması için Ayarlar > Entegrasyonlar'dan GA4 bağlantısı yapılmalı.": "This feature needs a GA4 connection from Settings > Integrations.",
  'Veri yüklenemedi': 'Could not load data',
});

// Sayfa animasyonları tek yerde; inline stil keyframe tanımlayamadığı için küçük bir <style>.
export function GrowthStyles() {
  return (
    <style>{`
      @keyframes gr-shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
      @keyframes gr-fade { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
      .gr-fade { animation: gr-fade .25s ease both; }
      .gr-hover { transition: border-color .15s, box-shadow .15s, transform .15s, background .15s; }
      .gr-hover:hover { border-color: var(--py-border-mid) !important; box-shadow: var(--py-shadow-hover); }
      .gr-row:hover { background: var(--py-subtle-bg) !important; }
      .gr-thin { scrollbar-width: thin; scrollbar-color: var(--py-border-mid) transparent; }
      .gr-thin::-webkit-scrollbar { width: 6px; height: 6px; }
      .gr-thin::-webkit-scrollbar-thumb { background: var(--py-border-mid); border: none; border-radius: 6px; }
      .gr-noscroll { scrollbar-width: none; }
      .gr-noscroll::-webkit-scrollbar { display: none; }
      .gr-details > summary { list-style: none; cursor: pointer; }
      .gr-details > summary::-webkit-details-marker { display: none; }
      .gr-details[open] .gr-chev { transform: rotate(180deg); }
    `}</style>
  );
}

export const cardStyle: React.CSSProperties = {
  border: `1px solid ${T.border}`, borderRadius: radius.xl, background: T.card, boxShadow: 'var(--py-shadow-card)',
};

export function GrowthHero({ icon, color, title, desc, right, isMobile }: {
  icon: React.ReactNode; color: string; title: string; desc: React.ReactNode; right?: React.ReactNode; isMobile: boolean;
}) {
  return (
    <div style={{
      ...cardStyle, position: 'relative', overflow: 'hidden', marginBottom: 16,
      padding: isMobile ? '16px' : '20px 22px',
      background: `linear-gradient(120deg, ${tint(color, 10)} 0%, var(--py-card) 55%)`,
      display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
      flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 14 : 20,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
        <span style={{ width: isMobile ? 42 : 48, height: isMobile ? 42 : 48, borderRadius: 14, flexShrink: 0,
          background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 6px 16px ${tint(color, 45)}` }}>{icon}</span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>{title}</div>
          <div style={{ fontSize: isMobile ? 12.5 : 13, color: T.textMid, lineHeight: 1.5, marginTop: 2 }}>{desc}</div>
        </div>
      </div>
      {right && <div style={{ flexShrink: 0, minWidth: 0 }}>{right}</div>}
    </div>
  );
}

export function SectionCard({ title, sub, right, children, pad = 18, style }: {
  title?: React.ReactNode; sub?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; pad?: number; style?: React.CSSProperties;
}) {
  return (
    <div style={{ ...cardStyle, padding: pad, ...style }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ minWidth: 0 }}>
            {title && <div style={{ fontSize: 15, fontWeight: 800, color: T.text, letterSpacing: '-0.01em' }}>{title}</div>}
            {sub && <div style={{ fontSize: 12, color: T.textDim, marginTop: 2, lineHeight: 1.45 }}>{sub}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

// Segment seçici — mobilde yatay kaydırılabilir, taşma yapmaz.
export function Segmented<K extends string | number>({ value, options, onChange, full }: {
  value: K; options: { key: K; label: React.ReactNode; color?: string }[]; onChange: (k: K) => void; full?: boolean;
}) {
  return (
    <div className="gr-noscroll" style={{ display: 'flex', gap: 3, background: T.trackBg, padding: 3, borderRadius: 11,
      width: full ? '100%' : 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
      {options.map(o => {
        const on = o.key === value;
        return (
          <button key={String(o.key)} onClick={() => onChange(o.key)} style={{
            flex: full ? 1 : undefined, padding: '7px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
            fontSize: 12.5, fontWeight: on ? 800 : 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
            background: on ? T.surface : 'transparent', color: on ? (o.color || T.text) : T.textMidAlt,
            boxShadow: on ? '0 1px 3px rgba(20,22,43,.12)' : 'none', transition: 'all .15s',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>{o.label}</button>
        );
      })}
    </div>
  );
}

export function Skeleton({ h = 14, w = '100%', r = 8, style }: { h?: number | string; w?: number | string; r?: number; style?: React.CSSProperties }) {
  return <div style={{ height: h, width: w, borderRadius: r, flexShrink: 0,
    background: 'linear-gradient(90deg, var(--py-track-bg) 0%, var(--py-subtle-bg) 50%, var(--py-track-bg) 100%)',
    backgroundSize: '800px 100%', animation: 'gr-shimmer 1.3s linear infinite', ...style }} />;
}

export function SkeletonPage({ isMobile, kpis = 4 }: { isMobile: boolean; kpis?: number }) {
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : `repeat(${kpis}, 1fr)`, gap: 12, marginBottom: 16 }}>
        {Array.from({ length: kpis }).map((_, i) => (
          <div key={i} style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <Skeleton h={11} w="55%" /><Skeleton h={24} w="70%" /><Skeleton h={8} />
          </div>
        ))}
      </div>
      <div style={{ ...cardStyle, padding: 18 }}>
        <Skeleton h={14} w={180} style={{ marginBottom: 16 }} />
        <Skeleton h={isMobile ? 220 : 320} r={12} />
      </div>
    </div>
  );
}

// GA4 bağlı değilse bunu söyler; başka bir hata ise gerçek mesajı gösterir
// (eskiden her hata "GA4 bağlı değil" diye görünüyordu).
export function ErrorState({ error }: { error: string }) {
  const { t } = useLang();
  const isGa4 = /ga4/i.test(error) && /bağlı|connect/i.test(error);
  return (
    <div className="gr-fade" style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '48px 20px', gap: 12, textAlign: 'center', borderStyle: 'dashed', boxShadow: 'none' }}>
      <span style={{ width: 52, height: 52, borderRadius: 16, background: isGa4 ? T.amberTint : T.redTint,
        color: isGa4 ? T.amberDeepText : T.redText, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {isGa4 ? <Icon.link size={24} sw={2} /> : <Icon.warningTriangle size={24} sw={2} />}
      </span>
      <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{isGa4 ? t('GA4 bağlı değil') : t('Veri yüklenemedi')}</div>
      <div style={{ fontSize: 13, color: T.textMid, maxWidth: 420, lineHeight: 1.55 }}>
        {isGa4 ? t("Bu özelliğin çalışması için Ayarlar > Entegrasyonlar'dan GA4 bağlantısı yapılmalı.") : error}
      </div>
    </div>
  );
}

export function Delta({ value, suffix, invert }: { value: number | null | undefined; suffix?: React.ReactNode; invert?: boolean }) {
  if (value == null) return null;
  const good = invert ? value < 0 : value >= 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 800,
      color: good ? T.greenText : T.redText }}>
      <span style={{ background: good ? T.greenTint : T.redTint, borderRadius: 999, padding: '2px 8px' }}>
        {value >= 0 ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
      </span>
      {suffix && <span style={{ color: T.textDim, fontWeight: 600 }}>{suffix}</span>}
    </span>
  );
}

export function LegendDot({ color, label, line, dashed }: { color: string; label: React.ReactNode; line?: boolean; dashed?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: T.textMid, fontWeight: 600 }}>
      {line
        ? <span style={{ width: 16, height: 0, borderTop: `2.5px ${dashed ? 'dashed' : 'solid'} ${color}`, display: 'inline-block' }} />
        : <span style={{ width: 9, height: 9, borderRadius: 3, background: color, display: 'inline-block' }} />}
      {label}
    </span>
  );
}

export const tooltipStyle: React.CSSProperties = {
  background: 'var(--py-surface)', border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 12px',
  fontSize: 12, boxShadow: 'var(--py-shadow-pop)', maxWidth: 240,
};
